var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var SmartMatcherConfig_1;
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { Column } from '@symon-ai/wisepipe-common-data';
import { JSON, JsonDiscriminatorValue, JsonSchema } from '@symon-ai/wisepipe-serialize';
import { ColumnFormat, CustomFormat, DefaultSettings } from './customFormat';
import './smartMatcherMatchColumns'; // force import for JsonSchema
import './smartMatcherTrainColumns'; // force import for JsonSchema
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { getRenamedColumns } from './toolConfigUtil';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationState } from './validation/toolValidationResult';
import { isSourceValid } from './validation/validateHelper';
export var SmartMatcherType;
(function (SmartMatcherType) {
    SmartMatcherType["ONE_TO_ONE"] = "one-to-one";
    SmartMatcherType["MANY_TO_ONE"] = "many-to-one";
    // MANY_TO_MANY = 'many-to-many'
})(SmartMatcherType || (SmartMatcherType = {}));
const schema = {
    $id: 'urn:wisepipe-common-pipe:SmartMatcherConfig',
    type: 'object',
    properties: {
        smartMatcherType: {
            type: 'string',
            enum: Object.values(SmartMatcherType)
        },
        trainColumn: { $ref: 'urn:wisepipe-common-pipe:SmartMatcherTrainColumns' },
        matchColumns: {
            type: 'array',
            items: { $ref: 'urn:wisepipe-common-pipe:SmartMatcherMatchColumns' },
            minItems: 1,
            uniqueItemProperties: ['leftColumn', 'rightColumn']
        }
    },
    required: ['smartMatcherType', 'trainColumn', 'matchColumns']
};
let SmartMatcherConfig = SmartMatcherConfig_1 = class SmartMatcherConfig extends ToolConfig {
    constructor(smartMatcherType, trainColumn, matchColumns, customFormats) {
        super(ToolType.SMART_MATCHER, customFormats);
        this.smartMatcherType = smartMatcherType;
        this.matchColumns = matchColumns;
        this.trainColumn = trainColumn;
        JSON.validate(this, SmartMatcherConfig_1);
    }
    // eslint-disable-next-line class-methods-use-this
    get left() {
        return SmartMatcherConfig_1.left;
    }
    // eslint-disable-next-line class-methods-use-this
    get right() {
        return SmartMatcherConfig_1.right;
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return SmartMatcherConfig_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return SmartMatcherConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return SmartMatcherConfig_1.outs;
    }
    static get left() {
        return SmartMatcherConfig_1.ins[0];
    }
    static get right() {
        return SmartMatcherConfig_1.ins[1];
    }
    static get out() {
        return SmartMatcherConfig_1.outs[0];
    }
    static get ins() {
        return ['left', 'right'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.SMART_MATCHER.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.COMBINE], this.ins, this.outs, ['match', 'entity resolution', 'dedupe', 'fuzzy', 'merge', 'combine'], false);
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const exp = tsb.export(this.out);
        const left = ins.schemas.at(this.left);
        const right = ins.schemas.at(this.right);
        const leftJoinCols = new Set();
        const rightJoinCols = new Set();
        this.matchColumns.forEach((join) => {
            leftJoinCols.add(join.leftColumn);
            rightJoinCols.add(join.rightColumn);
        });
        const leftColumnNames = left.map((col) => col.name);
        const rightColumnNames = right.map((col) => col.name);
        const probabilityColumnName = getRenamedColumns(leftColumnNames.concat(rightColumnNames), 'Probability', this.toolType);
        // generate the dependents, dependents would be the fuzzy match join columns on the left/right sides
        // the actual match column is not a dependant as it's only needed for training
        const dependents = [];
        left.forEach((colMet) => {
            if (leftJoinCols.has(colMet.name))
                dependents.push(colMet);
        });
        right.forEach((colMet) => {
            if (rightJoinCols.has(colMet.name))
                dependents.push(colMet);
        });
        // dedupe contains all left+right columns as they are not exact matches, but fuzzy matches
        const leftColumns = left.map((l) => l.name);
        const rightColumns = right.map((r) => r.name);
        const aliases = ToolConfig.computeAliases(leftColumns, rightColumns);
        left.forEach((c) => {
            if (leftJoinCols.has(c.name))
                exp.copy(this.left, c, false, true, dependents);
            else
                exp.copy(this.left, c, false, false);
        });
        right.forEach((c) => {
            if (rightJoinCols.has(c.name))
                exp.copy(this.right, c, false, true, dependents, aliases.get(c.name));
            else
                exp.copy(this.right, c, false, false, undefined, aliases.get(c.name));
        });
        const newColumnFormats = new Map();
        newColumnFormats.set(probabilityColumnName, new CustomFormat(ColumnFormat.Percentage, 0, DefaultSettings.thousandSeparator, true));
        exp
            .makeNew(new Column(probabilityColumnName, 'number'), dependents, true)
            .initializeColumnFormats()
            .setColumnFormats(newColumnFormats)
            .setColumnFormats(this.customFormats);
        return tsb.build();
    }
    isValid(scope, joinColumns) {
        const left = scope.schemas.at(this.left);
        const right = scope.schemas.at(this.right);
        if (!left || !right) {
            return ToolValidationState.PORT_MISSING_EDGES;
        }
        const leftColumnNames = left.map((col) => col.name);
        const rightColumnNames = right.map((col) => col.name);
        const probabilityColumnName = getRenamedColumns(leftColumnNames.concat(rightColumnNames), 'Probability', this.toolType);
        const outputs = [probabilityColumnName];
        // first check all left side join columns are provided
        const inputs = new Map();
        for (const joinCol of joinColumns) {
            const findLeft = left.find((col) => col.name === joinCol.leftColumn);
            if (!findLeft) {
                return ToolValidationState.BAD_METADATA;
            }
            if (findLeft.type === 'number' || findLeft.type === 'decimal') {
                inputs.set(joinCol.rightColumn, ['number', 'decimal']);
            }
            else {
                inputs.set(joinCol.rightColumn, [findLeft.type]);
            }
        }
        // ensure left side doesn't have name conflict with output columns
        for (const output of outputs) {
            if (left.find((col) => col.name === output) !== undefined)
                return ToolValidationState.BAD_METADATA;
        }
        // check that all right columns are properly provided, inputs has the list of right join columns
        return isSourceValid(right, inputs, outputs) ? ToolValidationState.VALID : ToolValidationState.BAD_METADATA;
    }
    isValidForCalc(iConfigHelper, scope) {
        // trainColumn is required for calc
        return this.isValid(scope, [...this.matchColumns, this.trainColumn]);
    }
    isValidForRun(iConfigHelper, scope, pass) {
        if (pass) {
            return scope.schemas.has(this.left) && scope.schemas.has(this.right)
                ? ToolValidationState.VALID
                : ToolValidationState.BAD_METADATA;
        }
        return this.isValid(scope, this.matchColumns);
    }
};
SmartMatcherConfig = SmartMatcherConfig_1 = __decorate([
    JsonSchema(schema),
    JsonDiscriminatorValue(ToolType.SMART_MATCHER)
], SmartMatcherConfig);
export { SmartMatcherConfig };
//# sourceMappingURL=smartMatcherConfig.js.map